import { type HttpErrorResponse } from '@angular/common/http';
import type { AsyncValidator, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import type { DomainCheckErrorResponse } from '@common/types/domain.type';

import { ChangeDetectorRef, Directive, Input, inject } from '@angular/core';
import { type AbstractControl, NG_ASYNC_VALIDATORS } from '@angular/forms';
import { type Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, finalize, filter } from 'rxjs/operators';

import { ESaveSubdomainErrorCodes } from '@common/enums/error-cores.enum';

import { ValidatorHttpService } from '@common/services/REST/validator.http.service';
import { isUuid } from '@common/helpers';

type SubDomainValidatorOptions = {
    siteId: string;
    checkIfDirty?: boolean;
};

let initialValue = null;
let initialFullDomain = null;

export function CheckSubDomainValidator(
    validatorHttpService: ValidatorHttpService,
    changeDetectorRef: ChangeDetectorRef,
    options: SubDomainValidatorOptions
): AsyncValidatorFn {
    return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> =>
        timer(500).pipe(
            filter(() => !!control.value),
            switchMap(() => {
                const domain = control.parent.get('domain').value?.name;

                if (!initialFullDomain) {
                    initialFullDomain = `${control.value}.${domain}`;
                }

                if (initialFullDomain === `${control.value}.${domain}`) {
                    return of(null);
                }

                if (options.siteId && !isUuid(options.siteId)) {
                    return validatorHttpService.checkSiteDomain(options.siteId, `${control.value}.${domain}`);
                }

                if (!initialValue) {
                    initialValue = control.value;
                }

                if (options.checkIfDirty && control.pristine && initialValue === control.value) {
                    return of(null);
                }

                return validatorHttpService.checkDomain({ domain: `${control.value}.${domain}` });
            }),
            map(() => null),
            catchError(({ error }: HttpErrorResponse) => {
                const errorCode = (error as DomainCheckErrorResponse)?.errors?.domain[0];

                const errorCodesMap = {
                    [ESaveSubdomainErrorCodes.INVALID_DOMAIN]: { [ESaveSubdomainErrorCodes.INVALID_DOMAIN]: true },
                    [ESaveSubdomainErrorCodes.DOMAIN_ALREADY_EXISTS]: {
                        [ESaveSubdomainErrorCodes.DOMAIN_ALREADY_EXISTS]: true,
                    },
                };

                if (!errorCode || !errorCodesMap[errorCode]) {
                    return null;
                }

                return of(errorCodesMap[errorCode]);
            }),
            finalize(() => changeDetectorRef.detectChanges())
        );
}

@Directive({
    selector: '[spSubDomainCheck][formControl]',
    providers: [{ provide: NG_ASYNC_VALIDATORS, useExisting: SubDomainValidatorDirective, multi: true }],
})
export class SubDomainValidatorDirective implements AsyncValidator {
    private readonly validatorHttpService = inject(ValidatorHttpService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    @Input() public spSubDomainOptions: SubDomainValidatorOptions = {
        siteId: null,
        checkIfDirty: false,
    };

    public validate(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
        return CheckSubDomainValidator(
            this.validatorHttpService,
            this.changeDetectorRef,
            this.spSubDomainOptions
        )(control);
    }
}
